/**
 * Angular dependencies
 */
import { Injectable } from "@angular/core";


/**
 * Third-party dependencies
 */
import { Observable, Subject, mergeMap, of } from "rxjs";
import { concatMap } from "rxjs/operators";


/**
 * Application dependencies
 */
import { featureFlags } from "@environments/environment";
import { AuthService } from "@services/auth/auth.service";
import { EventSourceOptions } from "../server-sent-events/sse.client";
import { Streamer } from "./streamer";

@Injectable({
  providedIn: 'root'
})
export class StreamingService extends Streamer {
  private readonly INVALID_JSON_OBJECT_RESPONSE = 'INVALID_JSON_OBJECT_RESPONSE';
  private abortController = new AbortController();

  accessToken$: Observable<string> = of('');

  constructor(
    private authService: AuthService,
  ) {
    super();
    this.accessToken$ = this.authService.getAccessToken();
  }

  override stream<T>(url: string, options: EventSourceOptions): Observable<T> {
    const props: RequestInit = {
      method: options.method || 'POST',
      headers: {
        ...options.headers,
        'Content-Type': 'application/json',
      },
      body: options.payload ?? undefined,
      signal: this.abortController.signal,
    };

    return this.fetch<T>(
      url,
      props,
    );
  }

  override disconnect(): void {
    this.abortController.abort();
  }

  private fetch = <T>(
    url: string,
    props: RequestInit,
  ): Observable<T> => {
    const handleLargeChunk = true;

    return this.updateHeaders(props).pipe(
      mergeMap((updatedProps) => fetch(url, updatedProps)),
      mergeMap((response: Response) => this.readAllChunks<T>(response, handleLargeChunk)),
    );
  };

  private updateHeaders(request: RequestInit): Observable<RequestInit> {
    const updatedRequest = {
      ...request,
      headers: {
        ...request.headers,
        "Accept": "text/event-stream",
      }
    };

    if (featureFlags.apiSixLogin) {
      return of({
        ...updatedRequest,
        credentials: 'include',
      });
    } else {
      return this.authService.getAccessToken().pipe(
        concatMap((token) => of({
          ...updatedRequest,
          headers: {
            ...updatedRequest.headers,
            "Authorization": `Bearer ${token}`,
          }
        }))
      );
    }
  }

  private isValidResponseObject = (response: any): boolean => {
    return response.parseStatus !== this.INVALID_JSON_OBJECT_RESPONSE;
  };

  private readAllChunks = <T>(
    response: Response,
    handleLargeChunk: boolean
  ): Observable<T> => {
    const stream = response.body;
    const reader = stream?.getReader();
    const subject = new Subject<T>();

    if (!response.ok) {
      subject.error(response)
    }
    let buffer = '';

    return this.readChunk(reader!, subject, buffer, handleLargeChunk);
  };

  private readChunk = <T>(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    subject: Subject<T>,
    buffer: string = '',
    handleLargeChunk: boolean
  ): Observable<T> => {
    reader?.read()
      .then(({ value, done }) => {
        if (done) {
          return subject.complete();
        }

        const decodedValue = new TextDecoder().decode(value).toString();

        if (handleLargeChunk) {
          let bufferData = '';

          if (decodedValue.endsWith('\n')) {
            buffer += decodedValue;
            bufferData = buffer;

            const rawResponseValue = this.getRawResponseValue(bufferData);

            rawResponseValue
              .map((line: string) => this.parseResponse<T>(line))
              .filter((response) => this.isValidResponseObject(response))
              .forEach((line) => subject.next(line));

            buffer = '';
          } else {
            buffer += decodedValue;
          }
          this.readChunk(reader, subject, buffer, handleLargeChunk);
        } else {
          const rawResponseValue = this.getRawResponseValue(decodedValue);

          rawResponseValue
            .map((line) => this.parseResponse<T>(line))
            .filter((response) => this.isValidResponseObject(response))
            .forEach((line) => subject.next(line));
          this.readChunk(reader, subject, buffer, handleLargeChunk);
        }
      })
      .catch((error) => {
        console.error('Error reading chunk', error);
        subject.error(error)
      });

    return subject.asObservable();
  };

  private getRawResponseValue = (bufferData: string): string[] => {
    return bufferData
      .split('\n')
      .filter((line) => line.trim() !== '' && line.startsWith('data:'))
      .map((line) => line.replaceAll('data:', ''))
      .filter((line) => line.trim() !== '');
  }

  private parseResponse = <T>(
    rawResponse: string,
  ): T => {
    return JSON.parse(rawResponse);
  };
}
